import React, { useEffect, useRef } from "react";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import { BookingGetters } from "../../../store/onlineBooking/getters";
import "./VehicleAvailabilityLoader.css";

interface VehicleAvailabilityLoaderProps {
  isLoading?: boolean;
  onComplete?: () => void;
}
import { TextResource } from "../../../@types/DTO/AppSetting";

export function VehicleAvailabilityLoader({
  isLoading = true,
  onComplete,
}: VehicleAvailabilityLoaderProps = {}) {
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const appSettings = BookingGetters.getAppSettings();

  const fillRef = useRef<HTMLDivElement | null>(null);
  const carRef = useRef<HTMLDivElement | null>(null);
  const motionRef = useRef<HTMLDivElement | null>(null);
  const prevLoadingRef = useRef<boolean>(isLoading);
  const finishStartedRef = useRef<boolean>(false);
  const onCompleteRef =
    useRef<VehicleAvailabilityLoaderProps["onComplete"]>(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = isLoading;

    if (!wasLoading || isLoading || finishStartedRef.current) return;
    finishStartedRef.current = true;

    const prefersReducedMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      onCompleteRef.current?.();
      return;
    }

    // Must match the animation-duration in the CSS keyframes above. Rate = 1/12s.
    const LOADING_DURATION_S = 12;
    const MIN_FINISH_S = 0.3;

    const parseMatrix = (t: string): number[] | null => {
      const m = t.match(/matrix\(([^)]+)\)/);
      if (!m) return null;
      return m[1].split(",").map((s) => parseFloat(s.trim()));
    };

    const freezeAndFinish = (
      el: HTMLElement | null,
      endTransform: string,
      remainingFrac: number
    ): number => {
      if (!el) return 0;
      const current = window.getComputedStyle(el).transform;
      el.style.animation = "none";
      if (current && current !== "none") {
        el.style.transform = current;
      }
      el.offsetHeight;
      const durationS = Math.max(
        MIN_FINISH_S,
        Math.min(LOADING_DURATION_S, remainingFrac * LOADING_DURATION_S)
      );
      el.style.transition = `transform ${durationS}s linear`;
      el.style.transform = endTransform;
      return durationS;
    };

    let fillRemainingFrac = 1;
    if (fillRef.current) {
      const parts = parseMatrix(
        window.getComputedStyle(fillRef.current).transform
      );
      const currentScale = parts ? parts[0] : 0;
      fillRemainingFrac = Math.max(0, Math.min(1, 1 - currentScale));
    }
    const fillDurationS = freezeAndFinish(
      fillRef.current,
      "scaleX(1)",
      fillRemainingFrac
    );

    const carFullPx = carRef.current?.offsetWidth ?? 0;
    let carRemainingFrac = 1;
    if (carRef.current && carFullPx > 0) {
      const parts = parseMatrix(
        window.getComputedStyle(carRef.current).transform
      );
      const currentTx = parts && parts.length >= 6 ? parts[4] : 0;
      carRemainingFrac = Math.max(
        0,
        Math.min(1, (carFullPx - currentTx) / carFullPx)
      );
    }
    const carDurationS = freezeAndFinish(
      carRef.current,
      `translateX(${carFullPx}px)`,
      carRemainingFrac
    );

    const motionFullPx = motionRef.current?.offsetWidth ?? 0;
    freezeAndFinish(
      motionRef.current,
      `translateX(${motionFullPx}px)`,
      carRemainingFrac
    );
    const maxDurationMs =
      Math.max(fillDurationS, carDurationS, MIN_FINISH_S) * 1000 + 50;
    window.setTimeout(() => {
      onCompleteRef.current?.();
    }, maxDurationMs);
  }, [isLoading]);
  const primary =
    theme?.palette?.primary?.main || appSettings?.primaryColor || "#9ca3af";

  const lang = i18n.language;
  const shortLang = lang?.split("-")[0];
  const textResource = BookingGetters.getTextResourceDetailsBasedOnLang();
  const langEntries =
    textResource?.[lang] ||
    (shortLang && shortLang !== lang ? textResource?.[shortLang] : undefined);

  const data: TextResource[] =
    langEntries?.flatMap((item: any) => item.data || []) || [];

  const loaderTextEntry = data.find(
    (item: TextResource) => item.key === "enableCustomLoaderText"
  );
  const titleText =
    loaderTextEntry?.body || translate("We're loading the available cars...");
  const descriptionText =
    loaderTextEntry?.description ||
    translate("One moment, we're looking for the best options for you.");

  return (
    <div
      className="flex flex-col justify-center items-center w-full"
      style={
        {
          "--vehicle-loader-color": primary,
          padding: "56px 16px",
        } as React.CSSProperties
      }
      role="status"
      aria-live="polite"
    >
      <div className="vehicle-loader-inner">
        <div className="vehicle-loader-rail">
          {/* motion lines trailing behind the car */}
          <div ref={motionRef} className="vehicle-loader-motion">
            <span className="vehicle-loader-motion-line" />
            <span className="vehicle-loader-motion-line" />
            <span className="vehicle-loader-motion-line" />
          </div>

          {/* car icon (line-art hatchback SVG, picks up currentColor) */}
          <div ref={carRef} className="vehicle-loader-car-runner">
            <svg
              className="vehicle-loader-car"
              viewBox="0 0 120 72"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M 12 52 L 12 42 Q 14 34 24 32 L 36 30 L 46 16 Q 48 12 54 12 L 72 12 C 92 12 108 28 108 44 L 108 52 L 97 52 A 7 7 0 0 1 83 52 L 37 52 A 7 7 0 0 1 23 52 Z"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              <path
                d="M 36 30 L 102 30"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              {/* wheels */}
              <circle
                cx="30"
                cy="56"
                r="8"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              />
              <circle
                cx="90"
                cy="56"
                r="8"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              />
            </svg>
          </div>

          {/* progress-bar track + fill */}
          <div className="vehicle-loader-track">
            <div ref={fillRef} className="vehicle-loader-fill" />
          </div>
        </div>

        <div className="vehicle-loader-copy">
          <div className="vehicle-loader-title">{titleText}</div>
          <div className="vehicle-loader-description">{descriptionText}</div>
        </div>
      </div>
    </div>
  );
}

export default VehicleAvailabilityLoader;
